/**
 * LLM-Powered Design System Analyzer
 * Uses Claude Code for deep, first-principles analysis of design system components
 */

import { ClaudeSubprocessClient } from '../security/claude-subprocess-client';
import { ContextGatherer, DesignSystemContext } from './context-gatherer';
import { ClassificationResult } from './intent-classifier';

export interface AnalysisRequest {
  message: string;
  focus: 'visual' | 'props' | 'accessibility' | 'tokens' | 'architecture' | 'general';
  currentStory?: string;
  component?: string;
  parameters: Record<string, any>;
}

export interface AnalysisResult {
  success: boolean;
  analysis: {
    summary: string;
    firstPrinciples: string[];
    designRationale: string;
    tokenUsage: string[];
    accessibility: string;
    improvements: string[];
    relationships: string[];
  };
  visualBreakdown?: {
    colors: Record<string, string>;
    spacing: Record<string, string>;
    typography: Record<string, string>;
    layout: string[];
  };
  suggestions: string[];
  actionButtons: Array<{
    label: string;
    action: string;
    parameters: Record<string, any>;
    variant: string;
  }>;
  metadata: {
    processingTime: number;
    contextUsed: string[];
    confidence: number;
  };
}

export class LLMAnalyzer {
  private claudeClient: ClaudeSubprocessClient;
  private contextGatherer: ContextGatherer;
  private designSystemKnowledge: string;

  constructor(projectPath: string) {
    this.claudeClient = new ClaudeSubprocessClient({
      timeout: 30000,
      debug: false
    });
    this.contextGatherer = new ContextGatherer(projectPath);
    
    // Load design system knowledge
    this.designSystemKnowledge = this.loadDesignSystemKnowledge();
  }

  /**
   * Perform comprehensive LLM-powered analysis
   */
  async analyzeStory(request: AnalysisRequest): Promise<AnalysisResult> {
    const startTime = Date.now();
    
    try {
      // 1. Gather comprehensive context
      const context = await this.contextGatherer.gatherContext();
      
      // 2. Build comprehensive prompt with context
      const analysisPrompt = this.buildAnalysisPrompt(request, context);
      
      // 3. Get LLM analysis
      const claudeResponse = await this.claudeClient.ask(analysisPrompt);
      
      // 4. Parse and structure response
      const analysis = this.parseAnalysisResponse(claudeResponse.output, request);
      
      // 5. Generate action buttons based on analysis
      const actionButtons = this.generateActionButtons(analysis, request);
      
      const processingTime = Date.now() - startTime;
      
      return {
        success: true,
        analysis,
        visualBreakdown: this.extractVisualBreakdown(analysis, context),
        suggestions: this.generateSuggestions(analysis, request),
        actionButtons,
        metadata: {
          processingTime,
          contextUsed: this.getContextSummary(context),
          confidence: this.calculateConfidence(analysis)
        }
      };
      
    } catch (error) {
      return {
        success: false,
        analysis: {
          summary: `Analysis failed: ${error}`,
          firstPrinciples: [],
          designRationale: '',
          tokenUsage: [],
          accessibility: '',
          improvements: [],
          relationships: []
        },
        suggestions: ['Please try rephrasing your question'],
        actionButtons: [],
        metadata: {
          processingTime: Date.now() - startTime,
          contextUsed: [],
          confidence: 0
        }
      };
    }
  }

  /**
   * Build comprehensive analysis prompt with full context
   */
  private buildAnalysisPrompt(request: AnalysisRequest, context: DesignSystemContext): string {
    const { message, focus, component, currentStory } = request;
    
    return `# Design System Analysis Request

## CONTEXT: You are an expert design system analyst with deep understanding of Dezzy's architecture.

## DESIGN SYSTEM KNOWLEDGE:
${this.designSystemKnowledge}

## PROJECT CONTEXT:
- **Components Available**: ${context.components.map(c => c.name).join(', ')}
- **Sections Available**: ${context.sections.map(s => s.name).join(', ')}
- **Views Available**: ${context.views.map(v => v.name).join(', ')}

## DESIGN TOKENS:
\`\`\`json
${JSON.stringify(context.tokens, null, 2)}
\`\`\`

${component ? `
## COMPONENT BEING ANALYZED: ${component}
${this.getComponentContext(component, context)}
` : ''}

${currentStory ? `
## CURRENT STORY: ${currentStory}
${this.getStoryContext(currentStory, context)}
` : ''}

## USER QUESTION: "${message}"
**Analysis Focus**: ${focus}

## ANALYSIS INSTRUCTIONS:

### 1. FIRST-PRINCIPLES REASONING
Break down the question to fundamental design principles:
- What is the user really asking about?
- What design system concepts are involved?
- How do tokens → elements → components → sections → views relate to this question?

### 2. COMPREHENSIVE ANALYSIS
Analyze from multiple perspectives:
- **Visual Design**: Colors, spacing, typography, layout
- **Token Usage**: Which design tokens are being used and why
- **Accessibility**: WCAG compliance, keyboard navigation, screen readers
- **Architecture**: How this fits in the design system hierarchy
- **User Experience**: Why design decisions were made this way

### 3. ACTIONABLE INSIGHTS
Provide specific, actionable insights:
- Design rationale with clear explanations
- Improvement suggestions with token references
- Relationship to other components/patterns
- Next steps for the user

## REQUIRED RESPONSE FORMAT:

Respond with a JSON object in this exact structure:
\`\`\`json
{
  "summary": "One-sentence summary of the analysis",
  "firstPrinciples": [
    "Fundamental principle 1 that applies",
    "Fundamental principle 2 that applies"
  ],
  "designRationale": "Detailed explanation of why the design works this way, referencing specific tokens and design system principles",
  "tokenUsage": [
    "primary.500: Used for primary action color",
    "space.md: Provides consistent padding"
  ],
  "accessibility": "Assessment of accessibility features and compliance",
  "improvements": [
    "Specific improvement suggestion 1",
    "Specific improvement suggestion 2"
  ],
  "relationships": [
    "How this relates to other components",
    "Patterns that are similar or different"
  ]
}
\`\`\`

**CRITICAL**: Respond ONLY with valid JSON. No additional text outside the JSON.`;
  }

  /**
   * Parse Claude's analysis response
   */
  private parseAnalysisResponse(response: string, request: AnalysisRequest): AnalysisResult['analysis'] {
    try {
      // Try to extract JSON from the response
      const jsonMatch = response.match(/```json\\s*({[\\s\\S]*?})\\s*```/) || response.match(/({[\\s\\S]*})/);
      
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]);
        return {
          summary: parsed.summary || 'Analysis completed',
          firstPrinciples: Array.isArray(parsed.firstPrinciples) ? parsed.firstPrinciples : [],
          designRationale: parsed.designRationale || '',
          tokenUsage: Array.isArray(parsed.tokenUsage) ? parsed.tokenUsage : [],
          accessibility: parsed.accessibility || '',
          improvements: Array.isArray(parsed.improvements) ? parsed.improvements : [],
          relationships: Array.isArray(parsed.relationships) ? parsed.relationships : []
        };
      }
    } catch (error) {
      console.warn('Failed to parse LLM JSON response:', error);
    }

    // Fallback to natural language parsing
    return this.parseNaturalLanguageResponse(response, request);
  }

  /**
   * Fallback natural language parsing
   */
  private parseNaturalLanguageResponse(response: string, request: AnalysisRequest): AnalysisResult['analysis'] {
    const lines = response.split('\\n').filter(line => line.trim());
    
    return {
      summary: lines[0] || 'Claude provided analysis of the component',
      firstPrinciples: this.extractListItems(response, 'principle'),
      designRationale: this.extractSection(response, 'rationale|reason|why'),
      tokenUsage: this.extractTokenReferences(response),
      accessibility: this.extractSection(response, 'accessibility|a11y|screen reader'),
      improvements: this.extractListItems(response, 'improve|suggest|recommend'),
      relationships: this.extractListItems(response, 'similar|relate|pattern')
    };
  }

  /**
   * Generate contextual action buttons
   */
  private generateActionButtons(analysis: AnalysisResult['analysis'], request: AnalysisRequest) {
    const buttons = [];

    // View related components
    if (analysis.relationships.length > 0) {
      buttons.push({
        label: 'View Related Components',
        action: 'search',
        parameters: { query: 'similar components', type: 'relationship' },
        variant: 'outline'
      });
    }

    // Token exploration
    if (analysis.tokenUsage.length > 0) {
      buttons.push({
        label: 'Explore Design Tokens',
        action: 'analyze',
        parameters: { focus: 'tokens', component: request.component },
        variant: 'secondary'
      });
    }

    // Accessibility details
    if (analysis.accessibility && request.focus !== 'accessibility') {
      buttons.push({
        label: 'Accessibility Analysis',
        action: 'analyze',
        parameters: { focus: 'accessibility', component: request.component },
        variant: 'secondary'
      });
    }

    // Implementation improvements
    if (analysis.improvements.length > 0) {
      buttons.push({
        label: 'View Improvements',
        action: 'improve',
        parameters: { suggestions: analysis.improvements },
        variant: 'primary'
      });
    }

    return buttons;
  }

  /**
   * Extract visual breakdown from analysis
   */
  private extractVisualBreakdown(analysis: AnalysisResult['analysis'], context: DesignSystemContext) {
    const breakdown: AnalysisResult['visualBreakdown'] = {
      colors: {},
      spacing: {},
      typography: {},
      layout: []
    };

    // Extract token references and map to actual values
    analysis.tokenUsage.forEach(tokenRef => {
      const [token, description] = tokenRef.split(':');
      const tokenPath = token.trim();

      if (tokenPath.includes('color') || tokenPath.includes('primary')) {
        breakdown.colors[tokenPath] = this.getTokenValue(tokenPath, context) || description?.trim() || '';
      } else if (tokenPath.includes('space')) {
        breakdown.spacing[tokenPath] = this.getTokenValue(tokenPath, context) || description?.trim() || '';
      } else if (tokenPath.includes('typography') || tokenPath.includes('font')) {
        breakdown.typography[tokenPath] = this.getTokenValue(tokenPath, context) || description?.trim() || '';
      }
    });

    return breakdown;
  }

  /**
   * Generate contextual suggestions
   */
  private generateSuggestions(analysis: AnalysisResult['analysis'], request: AnalysisRequest): string[] {
    const suggestions = [];

    suggestions.push(`**${analysis.summary}**`);

    if (analysis.firstPrinciples.length > 0) {
      suggestions.push('', '**Key Principles:**');
      suggestions.push(...analysis.firstPrinciples.map(p => `• ${p}`));
    }

    if (analysis.designRationale) {
      suggestions.push('', '**Design Rationale:**');
      suggestions.push(analysis.designRationale);
    }

    if (analysis.improvements.length > 0) {
      suggestions.push('', '**Improvements:**');
      suggestions.push(...analysis.improvements.map(i => `• ${i}`));
    }

    return suggestions;
  }

  // Helper methods
  private loadDesignSystemKnowledge(): string {
    // In a real implementation, this would load from the markdown file
    return `
    Dezzy Design System follows a strict hierarchy:
    1. TOKENS: Atomic design values (colors, spacing, typography)
    2. ELEMENTS: Basic HTML + tokens
    3. COMPONENTS: Interactive elements with props/variants
    4. SECTIONS: Domain compositions of components
    5. VIEWS: Complete pages using DSL

    Key principles:
    - Token-based styling (no raw values)
    - Accessibility-first (WCAG AA)
    - Dark mode support
    - Component composition over creation
    `;
  }

  private getComponentContext(componentName: string, context: DesignSystemContext): string {
    const component = context.components.find(c => c.name.toLowerCase() === componentName.toLowerCase());
    
    if (!component) return 'Component not found in current project';

    return `
**Props**: ${JSON.stringify(component.props, null, 2)}
**Variants**: ${component.variants.join(', ')}
**CSS Classes**: ${component.styling.classes.join(', ')}
**Token Usage**: ${component.styling.tokenUsage.join(', ')}
**Stories**: ${component.storybook.stories.join(', ')}
**Accessibility**: ARIA: ${component.accessibility.ariaSupport}, Keyboard: ${component.accessibility.keyboardNav}
`;
  }

  private getStoryContext(storyName: string, context: DesignSystemContext): string {
    // This would be enhanced to get actual Storybook story data
    return `Story context would be gathered from Storybook API`;
  }

  private getTokenValue(tokenPath: string, context: DesignSystemContext): string | null {
    const path = tokenPath.split('.');
    let current: any = context.tokens;

    for (const segment of path) {
      if (current && typeof current === 'object' && segment in current) {
        current = current[segment];
      } else {
        return null;
      }
    }

    return typeof current === 'object' ? current.value : String(current);
  }

  private extractListItems(text: string, pattern: string): string[] {
    const regex = new RegExp(`(?:${pattern})[^\\n]*\\n([\\s\\S]*?)(?=\\n\\n|$)`, 'i');
    const match = text.match(regex);
    
    if (match) {
      return match[1]
        .split('\\n')
        .map(line => line.replace(/^\\s*[-•*]\\s*/, '').trim())
        .filter(line => line.length > 0);
    }
    
    return [];
  }

  private extractSection(text: string, pattern: string): string {
    const regex = new RegExp(`(?:${pattern})[^\\n]*\\n([\\s\\S]*?)(?=\\n\\n|$)`, 'i');
    const match = text.match(regex);
    return match ? match[1].trim() : '';
  }

  private extractTokenReferences(text: string): string[] {
    const tokenRefs = [];
    
    // Look for token patterns like "primary.500", "space.md", etc.
    const tokenMatches = text.match(/\\b[a-zA-Z]+\\.[a-zA-Z0-9]+\\b/g);
    if (tokenMatches) {
      tokenRefs.push(...tokenMatches.map(match => `${match}: Design token reference`));
    }

    // Look for CSS variable references
    const cssVarMatches = text.match(/--[a-zA-Z-]+/g);
    if (cssVarMatches) {
      tokenRefs.push(...cssVarMatches.map(match => `${match}: CSS custom property`));
    }

    return [...new Set(tokenRefs)]; // Remove duplicates
  }

  private getContextSummary(context: DesignSystemContext): string[] {
    return [
      `${context.components.length} components`,
      `${context.sections.length} sections`,
      `${context.views.length} views`,
      `${Object.keys(context.tokens.colors || {}).length} color tokens`,
      `${Object.keys(context.tokens.space || {}).length} spacing tokens`
    ];
  }

  private calculateConfidence(analysis: AnalysisResult['analysis']): number {
    let confidence = 0.5; // Base confidence

    // Increase confidence based on analysis completeness
    if (analysis.designRationale.length > 50) confidence += 0.1;
    if (analysis.firstPrinciples.length > 0) confidence += 0.1;
    if (analysis.tokenUsage.length > 0) confidence += 0.1;
    if (analysis.accessibility.length > 10) confidence += 0.1;
    if (analysis.improvements.length > 0) confidence += 0.1;

    return Math.min(confidence, 1.0);
  }
}